import styled, { css } from 'styled-components';

import { BaseStyle, BaseStyleEnums } from '@nhl/nhl-breakaway-ui';

import { WithIsHome } from '~/core/styleTypes';
import { centeredFlex, SHARED_SIZES, WithEventType } from '~/core/tableColumns/scheduleTableColumns/styles';

import { getEventColor } from './utils';

export const SIZES = {
    dateHeader: {
        spacing: 32,
    },
    eventIndicator: {
        heightLarge: 60,
        heightSmall: 16,
    },
    gameLinks: {
        shadowSize: 20,
        iconSize: 50,
        maxSize: 340,
        minSize: 140,
        size: 210,
    },
    gameScore: {
        maxSize: 170,
        size: 150,
    },
    gameWinner: {
        height: 35,
        size: 240,
    },
    matchup: {
        heightLarge: 72,
        heightSmall: 50,
        size: 380,
        maxSize: 400,
    },
    tableHeader: {
        cellHeight: 52,
        height: 54,
    },
    time: {
        maxSize: 170,
        size: 150,
    },
};

export const DateHeaderContainer = styled.div<{ $isFullSeason: boolean }>`
    margin-top: ${SIZES.dateHeader.spacing}px;

    ${BaseStyle.Mixins.screenSizes.medium} {
        display: flex;
        align-items: center;
    }

    ${({ $isFullSeason }) =>
        $isFullSeason &&
        css`
            scroll-margin-top: 30px;
        `}
`;

export const HeaderContainer = styled.div`
    ${BaseStyle.Mixins.gaps.small};
    display: flex;
    flex-direction: column;
`;

export const DateHeader = styled.h3<{ $isLessThanMedium: boolean; $isLarge: boolean }>`
    scroll-margin-top: ${({ $isLarge, $isLessThanMedium }) =>
        $isLarge ? '135px' : $isLessThanMedium ? '130px' : '140px'};
    flex: 1;
`;

export const ReactTableContainer = styled.div<{ $multipleGameStates: boolean }>`
    border-collapse: collapse;
    margin-top: 24px;
    margin-bottom: 40px;
    width: 100%;

    .rt-table {
        display: flex;
        flex-direction: column;
        overflow-x: inherit;

        ${BaseStyle.Mixins.screenSizes.large} {
            overflow-x: auto;
        }
    }

    .rt-thead {
        .rt-tr {
            align-items: center;
            border-top: 1px solid ${({ theme }) => theme.Surface.border};
            border-bottom: 1px solid ${({ theme }) => theme.Surface.border};
            display: flex;
            ${BaseStyle.Mixins.fontStyles.ui['14b']}
            height: ${SIZES.tableHeader.height}px;
            line-height: initial;
            background-color: ${({ theme, $multipleGameStates }) =>
                $multipleGameStates ? theme.Surface.secondary : theme.Surface.primary};
        }
    }

    .rt-tbody {
        .rt-tr {
            align-items: center;
            border-bottom: 1px solid ${({ theme }) => theme.Surface.border};
            display: flex;
            min-height: ${SHARED_SIZES.tableBody.heightSmall}px;
        }

        .rt-tr:nth-child(odd) {
            background: ${({ theme }) => theme.Table.zebraStripe};
        }
    }

    .rt-tr {
        flex-direction: column;

        ${BaseStyle.Mixins.screenSizes.large} {
            flex-direction: row;
            justify-content: space-between;
        }

        ${BaseStyle.Mixins.screenSizes.extraLarge} {
            justify-content: initial;
        }
    }

    .scheduleHeader {
        display: none;

        ${BaseStyle.Mixins.screenSizes.large} {
            display: flex;
        }
    }

    ${BaseStyle.Mixins.screenSizes.small} {
        margin-bottom: 16px;
    }

    ${BaseStyle.Mixins.screenSizes.large} {
        margin-bottom: 32px;

        .rt-thead,
        .rt-tbody {
            min-width: calc(100vw - 30px);
            width: fit-content;
        }

        .rt-tr:nth-child(odd) {
            .gameLinksColumn {
                background: ${({ theme }) => theme.Table.zebraStripe};
            }
        }
    }

    ${BaseStyle.Mixins.screenSizes.large} {
        .rt-thead,
        .rt-tbody {
            min-width: initial;
            width: initial;
        }
    }

    /**
     * This is checking if the screen size is less than medium size.
     * Normally we would use our Mixins.screenSizes however this is a special case that got a thumbs up.
     * We may add this to Mixins.screenSizes if other developers say it will make their lives easier 
     * however for now it will only exist here.
     * 
     * In this use case React Table will set the min, max, and normal widths of the columns. 
     * On smaller screens we override/reset all the sizes/widths of the columns.
     * So the table will show appropriately
     * 
     * If we didn't use this breakpoint
     * 1. By default React Table sets width sizes
     * 2. We would override the above default, and override/reset those sizes/widths for every screen size
     * 3. On medium+ size screens we would then set the width sizes again (Like in step 1)
     * This creates an extra step and state which makes the code harder to debug and a little more complex 
     */
    @media (max-width: ${BaseStyleEnums.ScreenWidthBreakpoints.large - 1}px) {
        .scheduleColumn {
            ${centeredFlex};
            width: 100%;
            max-width: initial;
            min-width: initial;
            flex-direction: column;

            &.fullWidth {
                width: 100%;
            }
        }
    }
`;

export const Team = styled.div<WithIsHome>`
    align-items: center;
    display: flex;
    flex: 3;
    flex-direction: ${({ $isHome }) => ($isHome ? 'row-reverse' : 'initial')};
    justify-content: ${({ $isHome }) => ($isHome ? 'start' : 'end')};
    text-align: ${({ $isHome }) => ($isHome ? 'initial' : 'right')};

    .team-display-container {
        display: initial;
        flex: initial;
    }

    .team-display {
        padding: 0 14px;

        ${BaseStyle.Mixins.screenSizes.large} {
            padding: 0 16px;
        }
    }
`;

export const AtIndicator = styled.span`
    ${BaseStyle.Mixins.fontStyles.desktop.body2};
`;

export const TeamNameContainer = styled.span<WithIsHome & { $hasNoLogo: boolean }>`
    margin-left: ${({ $isHome, $hasNoLogo }) => ($hasNoLogo && $isHome ? '14px' : '0')};
    margin-right: ${({ $isHome, $hasNoLogo }) => ($hasNoLogo && !$isHome ? '14px' : '0')};
`;

export const TeamName = styled.span`
    ${BaseStyle.Mixins.fontStyles.desktop.subtitle2};
`;

export const OddDisplay = styled.div<{ $aligned: string }>`
    color: ${({ theme }) => theme.Text.secondary};
    ${BaseStyle.Mixins.fontStyles.ui['14m']}
    padding-top: 4px;
    text-align: ${({ $aligned }) => $aligned || 'left'};
`;

export const MatchUpColumn = styled.div<{
    $isSpecialEvent: boolean;
    $topMargin: number;
}>`
    ${centeredFlex};
    flex-direction: column;
    height: ${SIZES.matchup.heightSmall}px;
    width: 100%;
    margin-top: ${({ $isSpecialEvent }) => ($isSpecialEvent ? '16px' : '0')};
    margin-bottom: ${({ $isSpecialEvent }) => ($isSpecialEvent ? '12px' : '0')};
    margin-right: -15px;

    ${BaseStyle.Mixins.screenSizes.large} {
        height: initial;
        margin-top: ${({ $topMargin }) => `${$topMargin}px`};
        margin-bottom: 0;
    }
`;

export const NeutralSite = styled.div`
    display: none;
    margin-right: -15px;

    ${BaseStyle.Mixins.screenSizes.large} {
        display: flex;
    }
`;

export const TeamContainer = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
`;

export const NoGamesText = styled.div`
    padding: 32px 0;

    ${BaseStyle.Mixins.screenSizes.large} {
        padding: 48px 0 ${48 - SIZES.dateHeader.spacing}px 0;
    }
`;

export const WinningTeamGameScore = styled.span`
    ${BaseStyle.Mixins.fontStyles.ui['12b']}

    ${BaseStyle.Mixins.screenSizes.large} {
        ${BaseStyle.Mixins.fontStyles.ui['16b']}
    }
`;

export const LiveGameScore = styled.span`
    color: ${({ theme }) => theme.Utility.success};
`;

export const GameWinner = styled.div<{ $withPrefix?: boolean }>`
    ${centeredFlex};
    flex-direction: column;
    text-align: ${({ $withPrefix }) => ($withPrefix ? 'center' : 'left')};
    color: ${({ theme }) => theme.Text.secondary};
    height: ${SIZES.gameWinner.height}px;

    ${BaseStyle.Mixins.screenSizes.large} {
        color: ${({ theme }) => theme.Text.primary};
        display: initial;
        height: initial;
    }
`;

/**
 * Events
 */
export const EventContainer = styled.div<WithEventType>`
    display: none;
    padding: ${({ $eventType }) => ($eventType ? '12px 0' : '0')};

    ${BaseStyle.Mixins.screenSizes.large} {
        display: flex;
    }
`;

export const GameInfoColumn = styled.div`
    ${BaseStyle.Mixins.gaps.extraSmall}

    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4px 0 12px 0;
`;

export const EventIndicator = styled.div<WithEventType>`
    background: ${({ $eventType, theme }) => getEventColor($eventType, theme)};
    border-radius: 8px;
    height: ${SIZES.eventIndicator.heightLarge}px;
    margin-left: 8px;
    width: 4px;
`;

export const SpecialEventImage = styled.img`
    display: flex;
    width: 100%;
    max-width: 128px;
    justify-content: center;
    margin: 0 18px;

    ${BaseStyle.Mixins.screenSizes.medium} {
        margin: 0 23px;
    }

    ${BaseStyle.Mixins.screenSizes.large} {
        margin: 0 24px;
    }
`;

export const PlayoffSeriesContainer = styled.div`
    color: inherit;
`;
